import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'incorrectFlagTitle'
	| 'incorrectFlagLabel'
	| 'incorrectFlagTextareaLabel'
	| 'includeInputCheckbox'
	| 'enrollInResearchCheckbox'
	| 'privacyPolicyLink'
	| 'submitButton'
	| 'thankYouMessage'
	| 'dismissBanner'
	| 'feedbackTextRequired';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		incorrectFlagTitle: {
			id: 'ai-mate.aup-feedback-banner.incorrect-flag-title',
			defaultMessage: 'Did we flag your input incorrectly?',
			description:
				'Title text displayed at the top of an Acceptable Use Policy (AUP) feedback banner within the AI conversation interface when a user clicks the thumbs down button on an AUP error message. Shown as the heading of the feedback banner. The purpose is to ask the user if the content moderation system incorrectly flagged their input, providing an opportunity to report false positives.',
		},
		incorrectFlagLabel: {
			id: 'ai-mate.aup-feedback-banner.incorrect-flag-label',
			defaultMessage: 'Additional feedback',
			description: 'Label shown above the text area in the AUP feedback banner',
		},
		incorrectFlagTextareaLabel: {
			id: 'ai-mate.aup-feedback-banner.incorrect-flag-textarea-label',
			defaultMessage: 'Tell us more',
			description:
				'Label text displayed above or as placeholder text in a textarea input field in the Acceptable Use Policy (AUP) feedback banner within the AI conversation interface. Shown as a form field label or placeholder. The purpose is to prompt users to provide additional details about why they believe their input was incorrectly flagged, helping the development team improve the content moderation system.',
		},
		includeInputCheckbox: {
			id: 'ai-mate.aup-feedback-banner.include-input-checkbox',
			defaultMessage:
				'Yes, include recent messages from this conversation to help Atlassian debug issues. Atlassian may contact me in order to debug the reported issue.',
			description:
				'Checkbox label text displayed next to a checkbox in the Acceptable Use Policy (AUP) feedback banner within the AI conversation interface. Shown as the label text next to a checkbox. When checked, it includes recent conversation messages in the feedback submission and consents to being contacted by Atlassian for debugging purposes. The purpose is to allow users to opt-in to providing additional context and consenting to follow-up contact to help resolve the false positive issue.',
		},
		enrollInResearchCheckbox: {
			id: 'ai-mate.aup-feedback-banner.enroll-in-research-checkbox',
			defaultMessage:
				'Yes, Atlassian may contact me to learn more about my experience and improve Atlassian apps and services. I acknowledge the {privacyPolicyLink}.',
			description:
				'Checkbox label text displayed next to a checkbox in the Acceptable Use Policy (AUP) feedback banner within the AI conversation interface. Shown as the label text next to a checkbox. The privacyPolicyLink placeholder will be substituted with a clickable link to the Atlassian Privacy Policy. When checked, it consents to being enrolled in research activities and acknowledges the privacy policy. The purpose is to allow users to opt-in to participating in research to help improve Atlassian products and services.',
		},
		privacyPolicyLink: {
			id: 'ai-mate.aup-feedback-banner.privacy-policy-link',
			defaultMessage: 'Atlassian Privacy Policy',
			description:
				'Link text displayed on a hyperlink in the Acceptable Use Policy (AUP) feedback banner within the AI conversation interface that opens the Atlassian Privacy Policy. Shown as clickable link text. When clicked, it opens the privacy policy in a new tab or window. The purpose is to provide users with access to the privacy policy so they can understand how their data will be used before consenting to research participation.',
		},
		submitButton: {
			id: 'ai-mate.aup-feedback-banner.submit-button',
			defaultMessage: 'Submit feedback',
			description:
				"Button label displayed on the submit button in the Acceptable Use Policy (AUP) feedback banner within the AI conversation interface. Shown as the text on a button. When clicked, it submits the user's feedback about the incorrect flagging, along with any selected consent options. The purpose is to provide a clear call-to-action for users to submit their feedback to help improve the content moderation system.",
		},
		thankYouMessage: {
			id: 'ai-mate.aup-feedback-banner.thank-you-message',
			defaultMessage: 'Thank you for your feedback!',
			description:
				"Success message text displayed in the Acceptable Use Policy (AUP) feedback banner within the AI conversation interface after feedback has been successfully submitted. Shown as a confirmation message in the banner. The purpose is to acknowledge the user's feedback submission and express gratitude for their contribution to improving the system.",
		},
		dismissBanner: {
			id: 'ai-mate.aup-feedback-banner.dismiss-banner',
			defaultMessage: 'Dismiss',
			description:
				'Button label displayed on the dismiss button in the Acceptable Use Policy (AUP) feedback banner within the AI conversation interface. Shown as the text on a button. When clicked, it closes the feedback banner without submitting any feedback. The purpose is to provide users with a way to close the banner if they do not want to provide feedback.',
		},
		feedbackTextRequired: {
			id: 'ai-mate.aup-feedback-banner.feedback-text-required',
			defaultMessage: 'Please provide feedback before submitting',
			description: 'Error message shown when user tries to submit without providing feedback text',
		},
	});

export default message;
